import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Clock, MessageSquare, FileText, Package, Headphones } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { companyInfo } from '../mock';

export const Contact = () => {
  const { t } = useTranslation();
  
  return (
    <div className="flex flex-col">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-emerald-900 text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">{t('contact.subtitle')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6">{t('contact.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-300 max-w-3xl leading-relaxed">
            {t('contact.description')}
          </p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-white to-neutral-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16">
            {/* Contact Details */}
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 lg:mb-10 text-neutral-900">{t('contact.getInTouch')}</h2>
              <div className="space-y-4 sm:space-y-6">
                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="flex items-center text-sm sm:text-base lg:text-lg">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4">
                        <Mail className="text-emerald-600" size={20} />
                      </div>
                      <span>{t('contact.email')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-14 sm:pl-20">
                    <a href={`mailto:${companyInfo.email}`} className="text-emerald-600 hover:text-emerald-700 text-sm sm:text-base lg:text-lg font-medium break-all">
                      {companyInfo.email}
                    </a>
                    <p className="text-xs sm:text-sm text-neutral-600 mt-1 sm:mt-2">
                      {t('contact.productInquiriesDesc')}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="flex items-center text-sm sm:text-base lg:text-lg">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4">
                        <Phone className="text-emerald-600" size={20} />
                      </div>
                      <span>{t('contact.phone')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-14 sm:pl-20">
                    <a href={`tel:${companyInfo.phone}`} className="text-emerald-600 hover:text-emerald-700 text-sm sm:text-base lg:text-lg font-medium">
                      {companyInfo.phone}
                    </a>
                    <p className="text-xs sm:text-sm text-neutral-600 mt-1 sm:mt-2">
                      {t('contact.monday-friday')} 8:00 AM - 6:00 PM
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="flex items-center text-sm sm:text-base lg:text-lg">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4">
                        <MapPin className="text-emerald-600" size={20} />
                      </div>
                      <span>{t('contact.address')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-14 sm:pl-20">
                    <p className="text-sm sm:text-base lg:text-lg mb-1 sm:mb-2 text-neutral-800">{companyInfo.address}</p>
                    <p className="text-xs sm:text-sm text-neutral-600">
                      {t('contact.productInquiriesDesc')}
                    </p>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardHeader className="pb-2 sm:pb-4">
                    <CardTitle className="flex items-center text-sm sm:text-base lg:text-lg">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-emerald-100 flex items-center justify-center mr-3 sm:mr-4">
                        <Clock className="text-emerald-600" size={20} />
                      </div>
                      <span>{t('contact.businessHours')}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pl-14 sm:pl-20">
                    <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm">
                      <div className="flex justify-between">
                        <span className="font-semibold text-neutral-800">{t('contact.monday-friday')}</span>
                        <span className="text-neutral-600">8:00 AM - 6:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-neutral-800">{t('contact.saturday')}</span>
                        <span className="text-neutral-600">9:00 AM - 2:00 PM</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-semibold text-neutral-800">{t('contact.sunday')}</span>
                        <span className="text-neutral-600">{t('contact.closed')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Why Contact Us */}
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 lg:mb-10 text-neutral-900">{t('contact.howWeCanHelp')}</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-4 sm:p-6 lg:p-8 rounded-r-xl sm:rounded-r-2xl shadow-lg">
                  <div className="flex items-start mb-2 sm:mb-4">
                    <MessageSquare className="text-emerald-600 mr-3 sm:mr-4 mt-0.5" size={20} />
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl text-emerald-900">{t('contact.productInquiries')}</h3>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm lg:text-base leading-relaxed pl-8 sm:pl-9">
                    {t('contact.productInquiriesDesc')}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-4 sm:p-6 lg:p-8 rounded-r-xl sm:rounded-r-2xl shadow-lg">
                  <div className="flex items-start mb-2 sm:mb-4">
                    <FileText className="text-emerald-600 mr-3 sm:mr-4 mt-0.5" size={20} />
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl text-emerald-900">{t('contact.customOrders')}</h3>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm lg:text-base leading-relaxed pl-8 sm:pl-9">
                    {t('contact.customOrdersDesc')}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-4 sm:p-6 lg:p-8 rounded-r-xl sm:rounded-r-2xl shadow-lg">
                  <div className="flex items-start mb-2 sm:mb-4">
                    <Package className="text-emerald-600 mr-3 sm:mr-4 mt-0.5" size={20} />
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl text-emerald-900">{t('contact.bulkOrders')}</h3>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm lg:text-base leading-relaxed pl-8 sm:pl-9">
                    {t('contact.bulkOrdersDesc')}
                  </p>
                </div>
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-l-4 border-emerald-600 p-4 sm:p-6 lg:p-8 rounded-r-xl sm:rounded-r-2xl shadow-lg">
                  <div className="flex items-start mb-2 sm:mb-4">
                    <Headphones className="text-emerald-600 mr-3 sm:mr-4 mt-0.5" size={20} />
                    <h3 className="font-semibold text-base sm:text-lg lg:text-xl text-emerald-900">{t('contact.technicalSupport')}</h3>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm lg:text-base leading-relaxed pl-8 sm:pl-9">
                    {t('contact.technicalSupportDesc')}
                  </p>
                </div>
              </div>

              {/* Quick Response */}
              <div className="mt-6 sm:mt-8 bg-white rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg border-2 border-emerald-100">
                <h3 className="font-semibold text-base sm:text-lg lg:text-xl mb-2 sm:mb-3 text-neutral-900">{t('contact.quickResponse')}</h3>
                <p className="text-neutral-700 text-xs sm:text-sm lg:text-base leading-relaxed">
                  {t('contact.quickResponseDesc')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">{t('contact.readyToOrder')}</h2>
          <p className="text-base sm:text-lg lg:text-xl mb-6 sm:mb-8 lg:mb-10 text-emerald-50 leading-relaxed max-w-2xl mx-auto px-4 sm:px-0">
            {t('contact.readyToOrderDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 lg:gap-6 justify-center px-4 sm:px-0">
            <a 
              href={`mailto:${companyInfo.email}`}
              className="inline-flex items-center justify-center px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-white text-emerald-600 font-semibold rounded-lg sm:rounded-xl hover:bg-neutral-50 transition-all shadow-2xl hover:scale-105 text-sm sm:text-base"
            >
              <Mail className="mr-2 sm:mr-3" size={18} />
              {t('contact.sendEmail')}
            </a>
            <a 
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center px-6 sm:px-8 lg:px-10 py-3 sm:py-4 bg-emerald-700 text-white font-semibold rounded-lg sm:rounded-xl hover:bg-emerald-800 border-2 border-white transition-all shadow-2xl hover:scale-105 text-sm sm:text-base"
            >
              <Phone className="mr-2 sm:mr-3" size={18} />
              {t('contact.callNow')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;